import { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Form, Button } from 'react-bootstrap';
import { Modal } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileAlt, faPlus, faTruck, faDollarSign,
  faXmark, faFileInvoice, faPrint, faUpload, faMapMarkedAlt,
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { safeBlob } from '../utils/documents';
import { fmtNum } from '../utils/format';
import moment from 'moment';
import '../styles/BOLs.css';
import '../styles/modal-shared.css';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import DocumentViewerModal from './DocumentViewerModal';
import CreateBOLWizard from './CreateBOLWizard';

// ── Marker icon factory ─────────────────────────────────────────────────────
const createMarkerIcon = (type, isHighlighted) => {
  const cfg = {
    pick:         { bg: '#3b82f6', border: '#1d4ed8',  label: 'P' },
    inactivePick: { bg: '#93c5fd', border: '#3b82f6',  label: 'P' },
    drop:         { bg: '#10b981', border: '#059669',  label: 'D' },
    inactiveDrop: { bg: '#6ee7b7', border: '#10b981',  label: 'D' },
    driver:       { bg: '#f59e0b', border: '#d97706',  label: '🚚' },
  };
  const c = cfg[type] || { bg: '#64748b', border: '#475569', label: '•' };
  const sz = isHighlighted ? 22 : 14;
  const shadow = isHighlighted
    ? `box-shadow:0 0 0 5px ${c.bg}55,0 2px 8px rgba(0,0,0,0.3);`
    : 'box-shadow:0 2px 6px rgba(0,0,0,0.25);';
  return L.divIcon({
    html: `<div style="
      width:${sz}px;height:${sz}px;border-radius:50%;
      background:${c.bg};border:2px solid ${c.border};
      display:flex;align-items:center;justify-content:center;
      font-size:${Math.max(7, sz * 0.45)}px;color:white;font-weight:700;
      ${shadow}transition:all .15s ease;
    ">${c.label}</div>`,
    className: '',
    iconSize:   [sz, sz],
    iconAnchor: [sz / 2, sz / 2],
    popupAnchor:[0, -(sz / 2) - 4],
  });
};

// ── Auto-fit map bounds when markers change ──────────────────────────────────
function MapBoundsFitter({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => m.position));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
  }, [markers, map]);
  return null;
}

// ── BOLs component ──────────────────────────────────────────────────────────
function BOLs() {
  const { user } = useAuth();

  const [openBOLs,      setOpenBOLs]      = useState([]);
  const [completedBOLs, setCompletedBOLs] = useState([]);
  const [loading,       setLoading]       = useState(true);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBOL,     setSelectedBOL]     = useState(null);
  const [bolDetails,      setBolDetails]      = useState({ origin: {}, destination: {}, stops: [], freight: [], rates: [], documents: [] });

  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate:   moment().format('YYYY-MM-DD'),
  });

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument,  setSelectedDocument]  = useState(null);
  const [hoveredBOL,        setHoveredBOL]        = useState(null);
  const [bolPdfData,        setBolPdfData]         = useState(null);
  const [showCreateModal,   setShowCreateModal]    = useState(false);
  const [isMobile,          setIsMobile]           = useState(false);

  // ── lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    loadOpenBOLs();
    loadCompletedBOLs();
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── data loading ──────────────────────────────────────────────────────────
  const loadOpenBOLs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/bols/open');
      setOpenBOLs(res.data);
    } catch (err) {
      toast.error(`Failed to load open BOLs: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedBOLs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/bols/completed', {
        params: {
          startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
          endDate:   moment(dateRange.endDate).format('YYYY-MM-DD'),
          _t: Date.now().toString(),
        },
      });
      setCompletedBOLs(res.data);
    } catch (err) {
      toast.error(`Failed to load completed BOLs: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // ── map data ──────────────────────────────────────────────────────────────
  const mapMarkers = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const markers = [];
    openBOLs.forEach(bol => {
      const fromGeo = bol.fromGeo?.split(',');
      const toGeo   = bol.toGeo?.split(',');
      if (!fromGeo || !toGeo) return;
      const fLat = parseFloat(fromGeo[0]), fLng = parseFloat(fromGeo[1]);
      const tLat = parseFloat(toGeo[0]),   tLng = parseFloat(toGeo[1]);
      if (isNaN(fLat) || isNaN(fLng) || isNaN(tLat) || isNaN(tLng)) return;

      const bStatus = [1, 2, 3, 7];
      if (bStatus.includes(parseInt(bol.min_idstatus))) {
        markers.push({
          id: `${bol.idbols}-pick`,
          bolId: bol.idbols,
          position: [fLat, fLng],
          type: bol.PickDate > today ? 'inactivePick' : 'pick',
          bol,
        });
      }
      markers.push({
        id: `${bol.idbols}-drop`,
        bolId: bol.idbols,
        position: [tLat, tLng],
        type: bol.DropDate > today ? 'inactiveDrop' : 'drop',
        bol,
      });
      if (bol.drvLoc && bol.drvLoc !== ',' && bol.drvLoc !== '') {
        const dGeo = bol.drvLoc.split(',');
        const dLat = parseFloat(dGeo[0]), dLng = parseFloat(dGeo[1]);
        if (!isNaN(dLat) && !isNaN(dLng)) {
          markers.push({ id: `${bol.idbols}-driver`, bolId: bol.idbols, position: [dLat, dLng], type: 'driver', bol });
        }
      }
    });
    return markers;
  }, [openBOLs]);

  const mapLines = useMemo(() => {
    const lines = [];
    openBOLs.forEach(bol => {
      const fromGeo = bol.fromGeo?.split(',');
      const toGeo   = bol.toGeo?.split(',');
      if (!fromGeo || !toGeo) return;
      const fLat = parseFloat(fromGeo[0]), fLng = parseFloat(fromGeo[1]);
      const tLat = parseFloat(toGeo[0]),   tLng = parseFloat(toGeo[1]);
      if (isNaN(fLat) || isNaN(fLng) || isNaN(tLat) || isNaN(tLng)) return;
      lines.push({ id: bol.idbols, positions: [[fLat, fLng], [tLat, tLng]] });
    });
    return lines;
  }, [openBOLs]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyDateFilter = () => {
    if (dateRange.startDate <= dateRange.endDate) loadCompletedBOLs();
    else toast.error('Start date must be before end date');
  };

  const handleBOLClick = async (bol) => {
    try {
      setSelectedBOL(bol);
      const res = await api.get(`/api/bols/${bol.idbols}/details`);
      setBolDetails(res.data);
      setShowDetailModal(true);
    } catch (err) {
      toast.error(`Failed to load BOL details: ${err.message || 'Unknown error'}`);
    }
  };

  const handlePrintBOL = async () => {
    if (!selectedBOL) return;
    try {
      toast.loading('Generating BOL document…');
      const res = await api.post(`/api/bols/${selectedBOL.idbols}/print`, {
        includePOD: selectedBOL.idBOLStatus >= 4,
      }, { responseType: 'blob' });
      const contentType = res.headers['content-type'];
      const blob    = new Blob([res.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      const bolDoc  = { descr: `BOL ${selectedBOL.idbols}`, fileName: `BOL_${selectedBOL.idbols}.pdf`, blobUrl, type: contentType };
      setBolPdfData(bolDoc);
      toast.dismiss();
      toast.success('BOL generated successfully');
      setSelectedDocument(bolDoc);
      setShowDocumentModal(true);
    } catch (err) {
      toast.dismiss();
      toast.error(`Failed to generate BOL: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDocumentClick = async (doc, e) => {
    e.preventDefault();
    toast.loading('Loading document…');
    try {
      // Backend streams the blob — fetch as blob, create a local object URL
      const res = await api.get(`/api/bols/documents/${encodeURIComponent(doc.fileName)}`, { responseType: 'blob' });
      // Only a render-safe type survives into the object URL — see utils/documents.
      const blob    = safeBlob(res.data, res.headers['content-type'] || 'application/pdf');
      const blobUrl = URL.createObjectURL(blob);
      toast.dismiss();
      setSelectedDocument({ ...doc, blobUrl, url: null });
      setShowDocumentModal(true);
    } catch (err) {
      toast.dismiss();
      toast.error(`Failed to load document: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const loadingToast = toast.loading(`Uploading ${files.length} document(s)…`);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('descr', file.name);
        const res = await api.post(`/api/bols/${selectedBOL.idbols}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setBolDetails(prev => ({ ...prev, documents: [...prev.documents, res.data] }));
      }
      toast.success(`Successfully uploaded ${files.length} document(s)`);
    } catch (err) {
      toast.error(`Failed to upload document: ${err.message || 'Unknown error'}`);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleNewRequestClick = () => setShowCreateModal(true);
  const handleBOLCreated      = () => loadOpenBOLs();

  // ── utilities ─────────────────────────────────────────────────────────────
  const formatDate = (d) => d ? moment(d, 'YYYY-MM-DD').format('MM/DD/YY') : '';

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  const grandTotal = useMemo(() => {
    if (!bolDetails?.rates?.length) return 0;
    return bolDetails.rates.reduce((s, r) => s + (parseFloat(r.Qty) || 0) * (parseFloat(r.Rate) || 0), 0);
  }, [bolDetails?.rates]);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="px-2">
      <Row className="g-2">
        {/* ── Map (desktop only) ── */}
        {!isMobile && (
          <Col md={6} lg={4}>
            <div style={{ width: '100%', height: 475, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <MapContainer
                center={[35.221, -80.854]}
                zoom={7}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <MapBoundsFitter markers={mapMarkers} />

                {/* Route lines */}
                {mapLines.map(line => (
                  <Polyline
                    key={line.id}
                    positions={line.positions}
                    pathOptions={{
                      color: hoveredBOL?.idbols === line.id ? '#3b82f6' : '#94a3b8',
                      weight: hoveredBOL?.idbols === line.id ? 3 : 1.5,
                      dashArray: '6 4',
                      opacity: hoveredBOL?.idbols === line.id ? 1 : 0.5,
                    }}
                  />
                ))}

                {/* Markers */}
                {mapMarkers.map(m => (
                  <Marker
                    key={m.id}
                    position={m.position}
                    icon={createMarkerIcon(m.type, hoveredBOL?.idbols === m.bolId)}
                  >
                    <Popup>
                      <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                        <strong>BOL #{m.bol.idbols}</strong><br />
                        <span style={{ color: '#64748b' }}>{m.type.includes('drop') || m.type.includes('Drop') ? 'Dropoff' : m.type === 'driver' ? 'Driver' : 'Pickup'}</span><br />
                        {m.bol.PickName} → {m.bol.DropName}<br />
                        {m.bol.BOLStatus}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Col>
        )}

        {/* ── Open BOLs table ── */}
        <Col md={isMobile ? 12 : 6} lg={isMobile ? 12 : 8}>
          <Card className="shadow-sm mb-2" style={{ height: 475 }}>
            <Card.Header className="py-3 d-flex justify-content-between align-items-center">
              <h5 className="m-0 font-weight-bold text-primary">
                Open BOLs
                <small className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>Click for details</small>
              </h5>
              <Button variant="primary" size="sm" onClick={handleNewRequestClick}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                New Request
              </Button>
            </Card.Header>
            <Card.Body className="p-0" style={{ overflowY: 'auto' }}>
              <Table hover size="sm">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Pro #</th>
                    <th className="d-none d-md-table-cell">Origin</th>
                    <th className="d-none d-lg-table-cell">Date</th>
                    <th>Destination</th>
                    <th className="d-none d-lg-table-cell">Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openBOLs.map(bol => (
                    <tr
                      key={bol.idbols}
                      onClick={() => handleBOLClick(bol)}
                      onMouseEnter={() => { if (!isMobile) setHoveredBOL(bol); }}
                      onMouseLeave={() => { if (!isMobile) setHoveredBOL(null); }}
                      style={{ cursor: 'pointer', backgroundColor: hoveredBOL?.idbols === bol.idbols ? '#eff6ff' : 'inherit' }}
                    >
                      <td>{bol.idbols}</td>
                      <td className="d-none d-md-table-cell">{bol.PickName}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(bol.PickDate)}</td>
                      <td>{bol.DropName}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(bol.DropDate)}</td>
                      <td>{bol.final_status === 4 ? 'Out for Del.' : bol.BOLStatus}</td>
                    </tr>
                  ))}
                  {openBOLs.length === 0 && !loading && (
                    <tr><td colSpan="6" className="text-center py-3 text-muted fst-italic">No open BOLs found</td></tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Completed BOLs ── */}
      <Row className="g-2">
        <Col>
          <Card className="shadow-sm" style={{ height: 'calc(100vh - 515px)', minHeight: 300 }}>
            <Card.Header className="py-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="m-0 font-weight-bold text-primary">Completed BOLs</h5>
                <div className="d-flex gap-2 flex-wrap mt-2 mt-sm-0">
                  <div className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0 d-none d-sm-block">From:</Form.Label>
                    <Form.Control type="date" name="startDate" value={dateRange.startDate} onChange={handleDateRangeChange} size="sm" />
                  </div>
                  <div className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0 d-none d-sm-block">To:</Form.Label>
                    <Form.Control type="date" name="endDate" value={dateRange.endDate} onChange={handleDateRangeChange} size="sm" />
                  </div>
                  <Button variant="primary" size="sm" onClick={handleApplyDateFilter}>
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    Search
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ height: 'calc(100vh - 575px)', minHeight: 250, overflowY: 'auto' }}>
                <Table hover size="sm" className="mb-0">
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <tr>
                      <th>Pro #</th>
                      <th className="d-none d-md-table-cell">Origin</th>
                      <th className="d-none d-lg-table-cell">Date</th>
                      <th>Destination</th>
                      <th className="d-none d-lg-table-cell">Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedBOLs.map(bol => (
                      <tr key={bol.idbols} onClick={() => handleBOLClick(bol)} style={{ cursor: 'pointer' }}>
                        <td>{bol.idbols}</td>
                        <td className="d-none d-md-table-cell">{bol.PickName}</td>
                        <td className="d-none d-lg-table-cell">{formatDate(bol.PickDate)}</td>
                        <td>{bol.DropName}</td>
                        <td className="d-none d-lg-table-cell">{formatDate(bol.DropDate)}</td>
                        <td>{bol.BOLStatus}</td>
                      </tr>
                    ))}
                    {completedBOLs.length === 0 && !loading && (
                      <tr><td colSpan="6" className="text-center py-3 text-muted fst-italic">No completed BOLs in selected date range</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── BOL Detail Modal ── */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="xl"
        centered
        dialogClassName="detail-modal"
      >
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={faFileInvoice} />
            </div>
            <div className="modal-header-text">
              <h5>BOL #{selectedBOL?.idbols}</h5>
              {selectedBOL?.BOLStatus && (
                <p className="modal-subtitle">{selectedBOL.BOLStatus}</p>
              )}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Origin + Destination */}
          <div className="detail-info-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="detail-info-field">
              <div className="detail-info-label">Origin</div>
              <div className="detail-info-value">{bolDetails.origin.pickname || '—'}</div>
              {bolDetails.origin.pickaddress && (
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                  {bolDetails.origin.pickaddress}<br />
                  {bolDetails.origin.pickcity}, {bolDetails.origin.pickstate} {bolDetails.origin.pickzip}
                </div>
              )}
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Destination</div>
              <div className="detail-info-value">{bolDetails.destination.dropname || '—'}</div>
              {bolDetails.destination.dropaddress && (
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                  {bolDetails.destination.dropaddress}<br />
                  {bolDetails.destination.dropcity}, {bolDetails.destination.dropstate} {bolDetails.destination.dropzip}
                </div>
              )}
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Pickup Date</div>
              <div className="detail-info-value">{formatDate(bolDetails.origin.pickdate) || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Delivery Date</div>
              <div className="detail-info-value">{formatDate(bolDetails.destination.dropdate) || <span className="detail-info-value--muted">—</span>}</div>
            </div>
          </div>

          {/* Stops */}
          <div className="detail-section">
            <div className="detail-section-header">
              <span className="detail-section-title">Stops</span>
              {bolDetails.stops.length > 0 && (
                <span className="detail-section-total" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                  {bolDetails.stops.length} stop{bolDetails.stops.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className={`detail-section-body${bolDetails.stops.length > 5 ? ' detail-section-body--scroll' : ''}`}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Origin</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Received By</th>
                    <th>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {bolDetails.stops.length > 0 ? bolDetails.stops.map((stop, i) => (
                    <tr key={i}>
                      <td>{stop.pickname}</td>
                      <td>{stop.dropname}</td>
                      <td>{stop.legstatus}</td>
                      <td>{formatDate(stop.poddate)}</td>
                      <td>{stop.podtime}</td>
                      <td>{stop.podname}</td>
                      <td>
                        {stop.podSig && (
                          <img
                            src={`data:image/png;base64,${stop.podSig.replace('data:image/png;base64,', '')}`}
                            width="80" height="28" alt="Signature"
                            style={{ maxWidth: '100%' }}
                          />
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="detail-table-empty">No stops found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Freight */}
          <div className="detail-section">
            <div className="detail-section-header">
              <span className="detail-section-title">Freight</span>
              {bolDetails.freight.length > 0 && (
                <span className="detail-section-total" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                  {bolDetails.freight.length} item{bolDetails.freight.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className={`detail-section-body${bolDetails.freight.length > 5 ? ' detail-section-body--scroll' : ''}`}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>FID</th>
                    <th>Customer Ref</th>
                    <th>MAWB</th>
                    <th>HAWB</th>
                    <th>Description</th>
                    <th>Pcs</th>
                    <th>Wt</th>
                  </tr>
                </thead>
                <tbody>
                  {bolDetails.freight.length > 0 ? bolDetails.freight.map((f, i) => (
                    <tr key={i}>
                      <td>{f.idFreight}</td>
                      <td>{f.CustomerRef || '—'}</td>
                      <td>{f.MAWB || '—'}</td>
                      <td>{f.HAWB || '—'}</td>
                      <td>{f.FreightDesc || '—'}</td>
                      <td>{f.TotalPieces}</td>
                      <td>{fmtNum(f.TotalWeight)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="detail-table-empty">No freight found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rates + Documents */}
          <div className="detail-two-col">
            {/* Rates */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 5 }} />
                  Rates
                </span>
                {grandTotal > 0 && (
                  <span className="detail-section-total">{formatCurrency(grandTotal)}</span>
                )}
              </div>
              <div className={`detail-section-body${bolDetails.rates.length > 5 ? ' detail-section-body--scroll' : ''}`}>
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bolDetails.rates.length > 0 ? bolDetails.rates.map((rate, i) => (
                      <tr key={i}>
                        <td>{rate.Descr}</td>
                        <td>{rate.Qty}</td>
                        <td>{formatCurrency(rate.Rate)}</td>
                        <td>{formatCurrency(rate.Qty * rate.Rate)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="detail-table-empty">No rates found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Documents */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 5 }} />
                  Documents
                </span>
                {selectedBOL && bolDetails.origin && [1, 2, 3].includes(bolDetails.origin.idBOLStatus) && (
                  <button
                    className="detail-btn detail-btn--icon"
                    onClick={() => document.getElementById('bol-doc-upload').click()}
                    title="Upload document"
                  >
                    <FontAwesomeIcon icon={faUpload} />
                  </button>
                )}
              </div>
              <input
                type="file"
                id="bol-doc-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleDocumentUpload}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              <div className="detail-section-body">
                <ul className="detail-doc-list">
                  {/* Generated BOL always first */}
                  <li className="detail-doc-item" onClick={handlePrintBOL}>
                    <FontAwesomeIcon icon={faPrint} className="doc-icon" />
                    BOL {selectedBOL?.idbols}
                  </li>
                  {bolDetails.documents.map((doc, i) => (
                    <li key={i} className="detail-doc-item" onClick={(e) => handleDocumentClick(doc, e)}>
                      <FontAwesomeIcon icon={faFileAlt} className="doc-icon" />
                      {doc.descr}
                    </li>
                  ))}
                  {bolDetails.documents.length === 0 && (
                    <li className="detail-doc-item" style={{ color: '#94a3b8', cursor: 'default', fontStyle: 'italic' }}>
                      No additional documents
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button className="detail-btn detail-btn--secondary" onClick={() => setShowDetailModal(false)}>
            <FontAwesomeIcon icon={faXmark} />
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Document Viewer ── */}
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        document={selectedDocument}
      />

      {/* ── BOL Creation Wizard ── */}
      <CreateBOLWizard
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={handleBOLCreated}
      />
    </Container>
  );
}

export default BOLs;
